import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useSubscription } from '../context/SubscriptionContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { readJsonOnce } from '../utils/safeFetch';
import { VUMeter } from '../components/upload/VUMeter';
import { 
  Upload as UploadIcon, 
  FileAudio, 
  FileText, 
  X,
  Loader2,
  CheckCircle,
  Mic,
  Square,
  Pause,
  Play,
  Clock,
  AlertTriangle,
  Lock,
  Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Upload() {
  const navigate = useNavigate();
  const { isAtLimit, getUsage, plan } = useSubscription();
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioContextRef = useRef(null);
  const [analyser, setAnalyser] = useState(null);
  const [recordingMime, setRecordingMime] = useState('audio/webm');
  
  // Audio upload state
  const [audioFile, setAudioFile] = useState(null);
  const [audioTitle, setAudioTitle] = useState('');
  const [audioCompany, setAudioCompany] = useState('');
  const [audioDealStage, setAudioDealStage] = useState('Discovery');
  const [audioUploading, setAudioUploading] = useState(false);
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordingTitle, setRecordingTitle] = useState('');
  const [recordingCompany, setRecordingCompany] = useState('');
  const [recordingDealStage, setRecordingDealStage] = useState('Discovery');
  const [recordingUploading, setRecordingUploading] = useState(false);
  
  // Transcript upload state
  const [transcriptTitle, setTranscriptTitle] = useState('');
  const [transcriptCompany, setTranscriptCompany] = useState('');
  const [transcriptDealStage, setTranscriptDealStage] = useState('Discovery');
  const [transcript, setTranscript] = useState('');
  const [transcriptUploading, setTranscriptUploading] = useState(false);
  // When a transcript hash matches a previous upload, the backend returns
  // `duplicate: true` instead of re-running the AI analysis. We surface that
  // as an inline banner (instead of silently navigating) so the user knows
  // the result is restored from cache and that no quota was consumed.
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  // Fuzzy-match suggestion for existing deals — populated when the rep blurs
  // out of the transcript company-name field. If they accept a suggestion,
  // we capture the deal_id and POST to /deals/{id}/link-call after analysis
  // completes (instead of letting the backend create a brand-new deal).
  const [similarDeals, setSimilarDeals] = useState([]);
  const [selectedExistingDealId, setSelectedExistingDealId] = useState(null);
  const [searchingDeals, setSearchingDeals] = useState(false);

  const dealStages = ['Discovery', 'Qualification', 'Demo', 'Negotiation', 'Closed'];

  // Cleanup timer + audio context on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // ============== FILE UPLOAD ==============
  
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['.mp3', '.mp4', '.mpeg', '.mpga', '.m4a', '.wav', '.webm'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(fileExt)) {
      toast.error(`Invalid file type. Allowed: ${allowedTypes.join(', ')}`);
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      toast.error('File size exceeds 25MB limit');
      return;
    }

    setAudioFile(file);
    if (!audioTitle) {
      setAudioTitle(file.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleAudioUpload = async () => {
    if (!audioFile) {
      toast.error('Please select an audio file');
      return;
    }
    if (!audioTitle.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setAudioUploading(true);
    
    const formData = new FormData();
    formData.append('file', audioFile);
    formData.append('title', audioTitle);
    if (audioCompany) formData.append('company_name', audioCompany);
    formData.append('deal_stage', audioDealStage);

    try {
      const response = await fetch(`${API_URL}/api/calls/upload-audio`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const result = await response.json();
      toast.success('Call uploaded and analyzed successfully!');
      navigate(`/report/${result.call_id}`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload audio');
    } finally {
      setAudioUploading(false);
    }
  };

  const clearAudioFile = () => {
    setAudioFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // ============== BROWSER RECORDING ==============

  const pickRecorderMime = () => {
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
    if (typeof MediaRecorder === 'undefined') return null;
    return candidates.find((m) => MediaRecorder.isTypeSupported(m)) || '';
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mime = pickRecorderMime();
      if (mime === null) {
        stream.getTracks().forEach((t) => t.stop());
        toast.error('Recording is not supported in this browser.');
        return;
      }
      const mediaRecorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      const effectiveMime = (mediaRecorder.mimeType || mime || 'audio/webm').split(';')[0];
      setRecordingMime(effectiveMime);

      // Live level meter via Web Audio API
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const audioCtx = new AudioCtx();
        const source = audioCtx.createMediaStreamSource(stream);
        const node = audioCtx.createAnalyser();
        node.fftSize = 1024;
        node.smoothingTimeConstant = 0.6;
        source.connect(node);
        audioContextRef.current = audioCtx;
        setAnalyser(node);
      } catch (e) {
        console.warn('Level meter unavailable:', e);
      }

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: effectiveMime });
        setRecordedBlob(blob);

        // Stop all tracks + tear down meter
        stream.getTracks().forEach(track => track.stop());
        if (audioContextRef.current) {
          audioContextRef.current.close().catch(() => {});
          audioContextRef.current = null;
        }
        setAnalyser(null);
      };
      
      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      toast.success('Recording started');
    } catch (error) {
      console.error('Recording error:', error);
      toast.error('Could not access microphone. Please check permissions.');
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        timerRef.current = setInterval(() => {
          setRecordingTime(prev => prev + 1);
        }, 1000);
        setIsPaused(false);
        toast.info('Recording resumed');
      } else {
        mediaRecorderRef.current.pause();
        clearInterval(timerRef.current);
        setIsPaused(true);
        toast.info('Recording paused');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      clearInterval(timerRef.current);
      setIsRecording(false);
      setIsPaused(false);
      toast.success('Recording stopped');
    }
  };

  const clearRecording = () => {
    setRecordedBlob(null);
    setRecordingTime(0);
    audioChunksRef.current = [];
  };

  const blobToBase64 = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the recording'));
    reader.onloadend = () => resolve(String(reader.result).split(',')[1] || '');
    reader.readAsDataURL(blob);
  });

  const handleRecordingUpload = async () => {
    if (!recordedBlob || recordedBlob.size === 0) {
      toast.error('No recording to upload');
      return;
    }
    if (!recordingTitle.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setRecordingUploading(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10 * 60 * 1000);

    try {
      const base64data = await blobToBase64(recordedBlob);

      const response = await fetch(`${API_URL}/api/calls/upload-recording`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          title: recordingTitle,
          audio_base64: base64data,
          company_name: recordingCompany || null,
          deal_stage: recordingDealStage,
          duration_seconds: recordingTime,
          mime_type: recordingMime,
        })
      });

      const result = await readJsonOnce(response);
      if (!response.ok) {
        const detail = result && typeof result === 'object' ? result.detail : null;
        throw new Error(
          (typeof detail === 'string' && detail) ||
          (response.status === 413 ? 'Recording is too large to upload.' : `Upload failed (HTTP ${response.status})`)
        );
      }

      toast.success(result.duplicate ? 'Duplicate recording — opening existing analysis' : 'Recording transcribed and analyzed!');
      navigate(`/report/${result.call_id}`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.name === 'AbortError' ? 'Analysis timed out. Please try again.' : (error.message || 'Failed to upload recording'));
    } finally {
      clearTimeout(timeoutId);
      setRecordingUploading(false);
    }
  };

  // ============== TRANSCRIPT UPLOAD ==============

  const handleTranscriptUpload = async () => {
    if (!transcriptTitle.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!transcript.trim()) {
      toast.error('Please enter the transcript');
      return;
    }

    setTranscriptUploading(true);
    setDuplicateInfo(null);

    try {
      const response = await fetch(`${API_URL}/api/calls/upload-transcript`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: transcriptTitle,
          transcript: transcript,
          company_name: transcriptCompany || null,
          deal_stage: transcriptDealStage
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const result = await response.json();
      if (result.duplicate) {
        // Cache hit — surface the banner, don't navigate away. Quota was not
        // consumed; let the user decide whether to open the existing report.
        setDuplicateInfo({ callId: result.call_id });
        toast.success('Restored from cache — analysis already exists.');
      } else {
        // If the rep accepted a "similar deal" suggestion, attach this fresh
        // call to that existing deal instead of letting the backend auto-create
        // a new one. Best-effort — failures here don't block the redirect.
        if (selectedExistingDealId) {
          try {
            await fetch(`${API_URL}/api/deals/${selectedExistingDealId}/link-call`, {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ call_id: result.call_id }),
            });
          } catch (linkErr) {
            console.error('link-call failed:', linkErr);
          }
        }
        toast.success('Transcript analyzed successfully!');
        navigate(`/report/${result.call_id}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to analyze transcript');
    } finally {
      setTranscriptUploading(false);
    }
  };

  // Fuzzy-match the typed company name against existing deals for this user.
  // Triggered on blur of the transcript company field. Threshold 0.85 is set
  // on the backend; we only surface the banner when there's at least one hit.
  const searchSimilarDeals = async (name) => {
    const q = (name || '').trim();
    if (!q || q.length < 2) {
      setSimilarDeals([]);
      return;
    }
    setSearchingDeals(true);
    try {
      const r = await fetch(
        `${API_URL}/api/deals/search?q=${encodeURIComponent(q)}`,
        { credentials: 'include' }
      );
      if (!r.ok) return;
      const data = await r.json();
      setSimilarDeals(data.matches || []);
      setSelectedExistingDealId(null);
    } catch (e) {
      console.error('deal search failed:', e);
    } finally {
      setSearchingDeals(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-8" data-testid="upload-page">
        {/* Restored-from-cache banner — appears when an identical transcript
            was previously analyzed for this user. No quota consumed. */}
        {duplicateInfo && (
          <div
            className="flex items-center gap-3 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30"
            data-testid="restored-from-cache-banner"
          >
            <div className="w-9 h-9 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <Zap className="w-5 h-5 text-emerald-600" fill="currentColor" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground">
                Restored from cache
              </p>
              <p className="text-xs text-muted-foreground">
                Analysis already exists for this transcript — no quota consumed.
              </p>
            </div>
            <button
              onClick={() => navigate(`/report/${duplicateInfo.callId}`)}
              className="px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 transition-colors"
              data-testid="restored-from-cache-view-button"
            >
              View analysis
            </button>
            <button
              onClick={() => setDuplicateInfo(null)}
              className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-emerald-500/10 transition-colors"
              aria-label="Dismiss"
              data-testid="restored-from-cache-dismiss-button"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Usage limit banner */}
        {isAtLimit('call_analyses') && (
          <div className="flex items-center gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/30" data-testid="usage-limit-banner">
            <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">Call analysis limit reached</p>
              <p className="text-xs text-muted-foreground">
                You've used all {getUsage('call_analyses').limit} analyses on your {plan === 'free' ? 'Free' : plan} plan.
              </p>
            </div>
            <button
              onClick={() => navigate('/pricing')}
              className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
            >
              Upgrade
            </button>
          </div>
        )}

        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">
            Upload Sales Call
          </h1>
          <p className="text-muted-foreground mt-1">
            Record, upload audio, or paste a transcript for AI analysis
          </p>
        </div>

        {/* Upload Tabs */}
        <Tabs defaultValue="record" className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-card border border-border">
            <TabsTrigger 
              value="record" 
              className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary"
              data-testid="record-tab"
            >
              <Mic className="w-4 h-4 mr-2" />
              Record Call
            </TabsTrigger>
            <TabsTrigger 
              value="audio" 
              className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary"
              data-testid="audio-tab"
            >
              <FileAudio className="w-4 h-4 mr-2" />
              Upload Audio
            </TabsTrigger>
            <TabsTrigger 
              value="transcript"
              className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary"
              data-testid="transcript-tab"
            >
              <FileText className="w-4 h-4 mr-2" />
              Paste Transcript
            </TabsTrigger>
          </TabsList>

          {/* Record Tab */}
          <TabsContent value="record" className="mt-6">
            <Card className="bg-card border-border/50">
              <CardHeader>
                <CardTitle className="font-heading text-xl">Record Call</CardTitle>
                <CardDescription>
                  Record your sales call directly in the browser
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Recording Controls */}
                <div className="flex flex-col items-center p-8 rounded-xl bg-secondary/30 border border-border">
                  {/* Timer Display */}
                  <div className="mb-6">
                    <p className={`font-mono text-5xl font-bold ${
                      isRecording ? 'text-destructive' : 'text-foreground'
                    }`}>
                      {formatTime(recordingTime)}
                    </p>
                    {isRecording && (
                      <p className="text-center text-sm text-muted-foreground mt-2">
                        {isPaused ? 'Paused' : 'Recording...'}
                      </p>
                    )}
                  </div>

                  {isRecording && (
                    <div className="mb-6 w-full flex justify-center">
                      <VUMeter analyser={analyser} active={isRecording} paused={isPaused} />
                    </div>
                  )}

                  {/* Control Buttons */}
                  <div className="flex items-center gap-4">
                    {!isRecording && !recordedBlob && (
                      <Button
                        size="lg"
                        onClick={startRecording}
                        className="bg-destructive text-white hover:bg-destructive/90 h-16 w-16 rounded-full"
                        data-testid="start-recording-btn"
                      >
                        <Mic className="w-6 h-6" />
                      </Button>
                    )}

                    {isRecording && (
                      <>
                        <Button
                          size="lg"
                          variant="outline"
                          onClick={pauseRecording}
                          className="h-14 w-14 rounded-full"
                          data-testid="pause-recording-btn"
                        >
                          {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
                        </Button>
                        <Button
                          size="lg"
                          onClick={stopRecording}
                          className="bg-secondary text-foreground hover:bg-secondary/80 h-14 w-14 rounded-full"
                          data-testid="stop-recording-btn"
                        >
                          <Square className="w-5 h-5" />
                        </Button>
                      </>
                    )}

                    {recordedBlob && !isRecording && (
                      <Button
                        variant="outline"
                        onClick={clearRecording}
                        className="border-destructive text-destructive hover:bg-destructive/10"
                        data-testid="clear-recording-btn"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Clear Recording
                      </Button>
                    )}
                  </div>

                  {recordedBlob && (
                    <div className="mt-4 flex items-center gap-2 text-sm text-success">
                      <CheckCircle className="w-4 h-4" />
                      Recording ready ({formatTime(recordingTime)})
                    </div>
                  )}
                </div>

                {/* Recording Metadata */}
                {recordedBlob && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="recording-title">Call Title *</Label>
                      <Input
                        id="recording-title"
                        placeholder="e.g., Discovery call with Acme Corp"
                        value={recordingTitle}
                        onChange={(e) => setRecordingTitle(e.target.value)}
                        className="bg-background border-border"
                        data-testid="recording-title-input"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="recording-company">Company Name</Label>
                        <Input
                          id="recording-company"
                          placeholder="e.g., Acme Corp"
                          value={recordingCompany}
                          onChange={(e) => setRecordingCompany(e.target.value)}
                          className="bg-background border-border"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="recording-stage">Deal Stage</Label>
                        <Select value={recordingDealStage} onValueChange={setRecordingDealStage}>
                          <SelectTrigger className="bg-background border-border">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-border">
                            {dealStages.map(stage => (
                              <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <Button
                      onClick={handleRecordingUpload}
                      disabled={!recordingTitle.trim() || recordingUploading}
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow"
                      data-testid="upload-recording-btn"
                    >
                      {recordingUploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Transcribing & Analyzing...
                        </>
                      ) : (
                        <>
                          <UploadIcon className="w-4 h-4 mr-2" />
                          Analyze Recording
                        </>
                      )}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audio Upload Tab */}
          <TabsContent value="audio" className="mt-6">
            <Card className="bg-card border-border/50">
              <CardHeader>
                <CardTitle className="font-heading text-xl">Upload Audio File</CardTitle>
                <CardDescription>
                  Supported formats: MP3, MP4, WAV, M4A, WebM (max 25MB)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* File Drop Zone */}
                <div 
                  className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
                    audioFile 
                      ? 'border-primary/50 bg-primary/5' 
                      : 'border-border hover:border-primary/30 hover:bg-secondary/50'
                  }`}
                  onClick={() => !audioFile && fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".mp3,.mp4,.mpeg,.mpga,.m4a,.wav,.webm"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="audio-file-input"
                  />
                  
                  {audioFile ? (
                    <div className="flex items-center justify-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <FileAudio className="w-6 h-6 text-primary" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-foreground">{audioFile.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {(audioFile.size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearAudioFile();
                        }}
                        className="text-muted-foreground hover:text-destructive"
                        data-testid="clear-audio-btn"
                      >
                        <X className="w-5 h-5" />
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <div className="w-16 h-16 rounded-full bg-secondary mx-auto mb-4 flex items-center justify-center">
                        <UploadIcon className="w-8 h-8 text-muted-foreground" />
                      </div>
                      <p className="font-medium text-foreground mb-1">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-sm text-muted-foreground">
                        MP3, MP4, WAV, M4A, WebM up to 25MB
                      </p>
                    </div>
                  )}
                </div>

                {/* Metadata */}
                <div className="space-y-2">
                  <Label htmlFor="audio-title">Call Title *</Label>
                  <Input
                    id="audio-title"
                    placeholder="e.g., Discovery call with Acme Corp"
                    value={audioTitle}
                    onChange={(e) => setAudioTitle(e.target.value)}
                    className="bg-background border-border"
                    data-testid="audio-title-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="audio-company">Company Name</Label>
                    <Input
                      id="audio-company"
                      placeholder="e.g., Acme Corp"
                      value={audioCompany}
                      onChange={(e) => setAudioCompany(e.target.value)}
                      className="bg-background border-border"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="audio-stage">Deal Stage</Label>
                    <Select value={audioDealStage} onValueChange={setAudioDealStage}>
                      <SelectTrigger className="bg-background border-border">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {dealStages.map(stage => (
                          <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Upload Button */}
                <Button
                  onClick={handleAudioUpload}
                  disabled={!audioFile || !audioTitle.trim() || audioUploading}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow"
                  data-testid="upload-audio-btn"
                >
                  {audioUploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Transcribing & Analyzing...
                    </>
                  ) : (
                    <>
                      <UploadIcon className="w-4 h-4 mr-2" />
                      Upload & Analyze
                    </>
                  )}
                </Button>

                {audioUploading && (
                  <p className="text-sm text-muted-foreground text-center">
                    This may take a few minutes depending on the audio length
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transcript Tab */}
          <TabsContent value="transcript" className="mt-6">
            <Card className="bg-card border-border/50">
              <CardHeader>
                <CardTitle className="font-heading text-xl">Paste Transcript</CardTitle>
                <CardDescription>
                  Paste your call transcript directly for analysis
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Metadata */}
                <div className="space-y-2">
                  <Label htmlFor="transcript-title">Call Title *</Label>
                  <Input
                    id="transcript-title"
                    placeholder="e.g., Demo call with Beta Inc"
                    value={transcriptTitle}
                    onChange={(e) => setTranscriptTitle(e.target.value)}
                    className="bg-background border-border"
                    data-testid="transcript-title-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="transcript-company">Company Name</Label>
                    <Input
                      id="transcript-company"
                      placeholder="e.g., Beta Inc"
                      value={transcriptCompany}
                      onChange={(e) => {
                        setTranscriptCompany(e.target.value);
                        // Typing invalidates any previous suggestion.
                        if (similarDeals.length || selectedExistingDealId) {
                          setSimilarDeals([]);
                          setSelectedExistingDealId(null);
                        }
                      }}
                      onBlur={(e) => searchSimilarDeals(e.target.value)}
                      className="bg-background border-border"
                      data-testid="transcript-company-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="transcript-stage">Deal Stage</Label>
                    <Select value={transcriptDealStage} onValueChange={setTranscriptDealStage}>
                      <SelectTrigger className="bg-background border-border">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {dealStages.map(stage => (
                          <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Similar deal suggestion — appears after rep blurs the
                    company name field if any existing deal has ≥0.85 similarity. */}
                {similarDeals.length > 0 && (
                  <div
                    className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30 space-y-3"
                    data-testid="similar-deal-banner"
                  >
                    {selectedExistingDealId ? (
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">
                              This call will be added to{' '}
                              <span className="font-semibold">
                                {similarDeals.find(d => d.deal_id === selectedExistingDealId)?.company_name}
                              </span>
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setSelectedExistingDealId(null)}
                          className="text-xs text-muted-foreground hover:text-foreground underline shrink-0"
                          data-testid="similar-deal-undo"
                        >
                          Undo
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                          <div className="text-sm">
                            <p className="font-medium text-foreground">
                              We found a similar deal:{' '}
                              <span className="font-semibold">{similarDeals[0].company_name}</span>
                              {similarDeals[0].call_count > 0 && (
                                <span className="text-muted-foreground font-normal">
                                  {' '}({similarDeals[0].call_count} call{similarDeals[0].call_count === 1 ? '' : 's'})
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Add this call to that deal instead?
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2 pl-7">
                          <button
                            onClick={() => setSelectedExistingDealId(similarDeals[0].deal_id)}
                            className="px-3 py-1.5 rounded-md bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors"
                            data-testid="similar-deal-yes"
                          >
                            Yes, link it
                          </button>
                          <button
                            onClick={() => setSimilarDeals([])}
                            className="px-3 py-1.5 rounded-md border border-border text-xs font-medium text-foreground hover:bg-muted transition-colors"
                            data-testid="similar-deal-no"
                          >
                            No, create new
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Transcript Input */}
                <div className="space-y-2">
                  <Label htmlFor="transcript">Transcript *</Label>
                  <Textarea
                    id="transcript"
                    placeholder={`Paste your transcript here...\n\nExample format:\n[Sales Rep]: Hi, thanks for meeting with me today...\n[Buyer]: Thanks for having me. We've been looking for a solution...`}
                    value={transcript}
                    onChange={(e) => setTranscript(e.target.value)}
                    className="min-h-[300px] bg-background border-border font-mono text-sm"
                    data-testid="transcript-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Tip: Include speaker labels like [Sales Rep] and [Buyer] for better analysis
                  </p>
                </div>

                {/* Analyze Button */}
                <Button
                  onClick={handleTranscriptUpload}
                  disabled={!transcript.trim() || !transcriptTitle.trim() || transcriptUploading}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow"
                  data-testid="analyze-transcript-btn"
                >
                  {transcriptUploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing Transcript...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Analyze Transcript
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Tips */}
        <Card className="bg-card/50 border-border/30">
          <CardContent className="p-6">
            <h3 className="font-heading text-lg font-semibold text-foreground mb-3">
              Tips for Best Results
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-success mt-0.5 shrink-0" />
                <span>Use clear audio recordings with minimal background noise</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-success mt-0.5 shrink-0" />
                <span>Include speaker labels in transcripts for accurate role detection</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-success mt-0.5 shrink-0" />
                <span>Full conversations provide more comprehensive analysis</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-success mt-0.5 shrink-0" />
                <span>Analysis includes signals, scoring, objections, risk radar, and coaching</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
